import * as React from 'react';
import { observer } from 'src/utils/mobx-react';
import { I18n } from 'src/domains/layouts/webview/components/language/I18n';
import { StakeInputMobx } from 'src/domains/sportsbook/betting/ui/betSlip/stakeInputMobx/StakeInputMobx';
import { BetslipMultibetId } from 'src/domains/sportsbook/betting/models/BetslipIdModels';
import { ReturnValue } from 'src/domains/sportsbook/betting/ui/betSlip/betslipCombination/BetslipCombination.style';
import {
    BetslipYouDecideRabItemsWrapper,
    RabItems,
    BetslipYouDecideEventReturn,
    BetslipYouDecideInputWrapper,
    BetslipYouDecideReturn,
    RabItemsWrapper,
    RabItemsWrapperSelection,
    BetslipYouDecideEventSuspendedBets,
    RabItemsWrapperMarket,
    ReturnsStyle,
    ReturnWrapper,
    RabItemsWrapperOdds,
} from 'src/domains/sportsbook/betting/ui/betSlip/BetslipShared.style';
import { useCommon } from 'src/domains/common/Common';
import { RewardWalletIcon } from 'src/domains/layouts/shared/assets/iconography/betslip/RewardWallet';
import {
    AppliedFreeBetContainer,
    BonusButton,
} from 'src/domains/sportsbook/betting/ui/betSlip/betslipSelection/BetslipSelection.style';
import { BasicDataModel } from 'src/domains/players/state/BasicDataModel';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { Amount } from 'src_common/common/amount/Amount';
import { AppSportsBookState } from 'src/domains/sportsbook/state/AppSportsBook.state';

interface PropsType {
    multibetSelectionId: BetslipMultibetId;
    className?: string;
}

export const BetslipMultibetItem = observer('BetslipMultibetItem', (props: PropsType) => {
    const common = useCommon();
    const { money } = BasicDataModel.get(common);
    const configState = ConfigComponents.get(common).config;
    const { className, multibetSelectionId } = props;
    const {
        getSelectionAppliedBonuses,
        openBonusPopup,
        betSlipSummaryState: { betTooHighValue },
        possibleBetsRequestState: { coreMultibetPossibleBetsResponse },
    } = AppSportsBookState.get(common).betSlipState;
    const model = multibetSelectionId.getModel();
    const freeBets = model.getFreeBets(coreMultibetPossibleBetsResponse);
    const appliedBonusFreeBetAmount = getSelectionAppliedBonuses(model.correlationId);

    const input = <StakeInputMobx selectionId={model.id} />;

    const isStake = model.inputField.isGreaterThanZero();
    const currentPriceResponse = model.currentPriceResponse;
    const isValidCurrentPriceResponse = currentPriceResponse !== null && currentPriceResponse.type === 'ok';
    const returnMultibetPrice = isValidCurrentPriceResponse
        ? new Amount(currentPriceResponse.data.payout.toString())
        : null;
    const multibetPriceLabel = isValidCurrentPriceResponse ? currentPriceResponse.data.priceFractional : 'N/A';
    const items = model.multibetOffer;
    const isBetWithoutOffer = items.length === 0;
    const setRef = AppSportsBookState.get(common).betSlipState.betslipData.setRef(multibetSelectionId);

    return (
        <>
            {
                <div className={className}>
                    <BetslipYouDecideRabItemsWrapper>
                        <RabItems>
                            {items.map((item, id) => {
                                const selection = item.selection;
                                return (
                                    <RabItemsWrapper key={id}>
                                        <RabItemsWrapperSelection data-test='selection-name'>
                                            {selection.selectionName || selection.selectionId}
                                        </RabItemsWrapperSelection>
                                        <RabItemsWrapperMarket data-test='market-name'>
                                            {selection.marketName || `Market: ${selection.sportsbookMarketTypeId}`}
                                        </RabItemsWrapperMarket>
                                    </RabItemsWrapper>
                                );
                            })}
                        </RabItems>
                        <RabItemsWrapperOdds data-test='multibet-price'>
                            Total odds: {multibetPriceLabel}
                        </RabItemsWrapperOdds>
                    </BetslipYouDecideRabItemsWrapper>
                </div>
            }
            <BetslipYouDecideInputWrapper
                ref={setRef}
                variant={configState.componentVariants.betslipVariant}
            >
                {input}
                {common.features.bonusEngine && freeBets.length > 0 && (
                    <BonusButton
                        size='medium'
                        dataTest='multibet-bets-bonus-button'
                        onClick={(): Promise<void> => openBonusPopup(model.correlationId, freeBets)}
                    >
                        <RewardWalletIcon />
                    </BonusButton>
                )}
            </BetslipYouDecideInputWrapper>
            {common.features.bonusEngine && appliedBonusFreeBetAmount !== null && (
                <AppliedFreeBetContainer
                    data-test='applied-free-bet-container'
                    variant={configState.componentVariants.betslipVariant}
                >
                    <RewardWalletIcon /> {money(appliedBonusFreeBetAmount)} Free Bet Applied
                </AppliedFreeBetContainer>
            )}

            {model.multibetStatus.suspendedBets || model.multibetStatus.expiredBets || isBetWithoutOffer ? (
                <BetslipYouDecideEventSuspendedBets>
                    {model.multibetStatus.expiredBets
                        ? model.multibetStatus.expiredMessage ?? 'Market data has expired. Please rebuild your bet.'
                        : 'That selection is no longer available. Please resolve to continue.'}
                </BetslipYouDecideEventSuspendedBets>
            ) : (
                <ReturnWrapper data-test='returns'>
                    <BetslipYouDecideEventReturn>
                        <I18n
                            langKey='betslip.you-decide.returns'
                            defaultText='Returns:'
                        />
                        <BetslipYouDecideReturn>
                            {' '}
                            {returnMultibetPrice === null ? null : money(returnMultibetPrice)}
                        </BetslipYouDecideReturn>
                    </BetslipYouDecideEventReturn>
                    {betTooHighValue !== null && isStake ? (
                        <ReturnsStyle>
                            <I18n
                                langKey='betslip.max-bet.label'
                                defaultText='Max. bet:'
                            />

                            <ReturnValue variant={configState.componentVariants.betslipVariant}>
                                {money(betTooHighValue)}
                            </ReturnValue>
                        </ReturnsStyle>
                    ) : null}
                </ReturnWrapper>
            )}
        </>
    );
});
